import React, { useEffect, useReducer, useRef } from "react";
import { reducer } from "./reducer";
import { AppStoreCtxProvider } from "./app-store-ctx-provider";
import { useUserAspect } from "../aspects/user-aspect/use-user-aspect";
import { useEnvironment } from "../../hooks/use-environment";
import { PublicConfigurationT, SwarovskiOptikSegmentKey } from "@swo/shared";
import Cookies from "js-cookie";
import {
  LOCALE_MISMATCH_COOKIE,
  PREFERRED_LOCALE_COOKIE_KEY,
  PREFERRED_SEGMENT_KEY_COOKIE_KEY,
  PREFERRED_SEGMENT_SELECTOR_COOKIE_KEY,
} from "@swo/shared";
import { StoreState } from "./state";

export const AppStore: React.FC<
  React.PropsWithChildren<{
    authServiceHost: URL;
    config: PublicConfigurationT;
    locationOrigin: URL;
    preferredLocaleCode: string | undefined;
    preferredSegmentKey: SwarovskiOptikSegmentKey | undefined;
    shouldDisplaySegmentPickerOverlay: boolean;
    withLocaleMismatchOverlay: boolean;
  }>
> = props => {
  const env = useEnvironment();

  const [state, dispatch] = useReducer(
    reducer,
    {
      segment: props.preferredSegmentKey,
      locale: props.preferredLocaleCode,
    },
    ({
      segment,
      locale,
    }: {
      segment: SwarovskiOptikSegmentKey | undefined;
      locale: string | undefined;
    }): StoreState => ({
      userPreferredSegmentKey: segment,
      userPreferredLocale: locale,
      showSegmentSelector: props.shouldDisplaySegmentPickerOverlay,
      withLocaleMismatchOverlay: props.withLocaleMismatchOverlay,
      authServiceHost: props.authServiceHost,
      config: props.config,
      locationOrigin: props.locationOrigin,
      headerFloatingDisabled: false,
    }),
  );

  const userAspect = useUserAspect();

  useEffect(() => {
    if (
      state.userPreferredSegmentKey &&
      Cookies.get(PREFERRED_SEGMENT_KEY_COOKIE_KEY) !== state.userPreferredSegmentKey
    ) {
      Cookies.set(PREFERRED_SEGMENT_KEY_COOKIE_KEY, state.userPreferredSegmentKey, { expires: 365 });
    }
  }, [state.userPreferredSegmentKey, env]);

  useEffect(() => {
    if (state.userPreferredLocale && Cookies.get(PREFERRED_LOCALE_COOKIE_KEY) !== state.userPreferredLocale) {
      Cookies.set(PREFERRED_LOCALE_COOKIE_KEY, state.userPreferredLocale, { expires: 365 });
      Cookies.remove(LOCALE_MISMATCH_COOKIE);
    }
  }, [state.userPreferredLocale, env]);

  useEffect(() => {
    let ignore = false;

    const c = Cookies.get(PREFERRED_SEGMENT_SELECTOR_COOKIE_KEY);

    if (c) {
      Cookies.remove(PREFERRED_SEGMENT_SELECTOR_COOKIE_KEY);

      if (!state.showSegmentSelector) {
        dispatch({ type: "user-landed-in-non-localized-page" });
      }

      return () => {
        ignore = true;
      };
    }
  }, []);

  return (
    <AppStoreCtxProvider state={state} userAspect={userAspect} dispatch={dispatch}>
      {props.children}
    </AppStoreCtxProvider>
  );
};
